import { getSnapshot } from 'mobx-state-tree';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import {
  Grid,
  InputGroup,
  Dialog,
  Heading,
  majorScale,
  FormField,
  defaultColors,
  Button,
  TagInput,
  TextArea,
  minorScale,
  useToast,
} from '../../npaper';
import { UsersInput } from '../../npaper/UserSelect';
import { IContact, IContactSnapshotIn, useStores } from '../../stores';
import { IRelation } from '../../stores/contacts/RelationStore';
import validateEmail from '../../utils/email-validator';
import { CompanySelect } from '../companies/CompanySelect';

interface FormValues extends Partial<IContactSnapshotIn> {
  company?: number | string;
  title?: string;
}

export interface IContactDetailsDialogProps {
  isShown: boolean;
  onClose: (contact?: IContact) => void;
  contact?: IContact;
  defaultValue?: FormValues;
}

export const ContactDetailsDialog: React.FC<IContactDetailsDialogProps> = ({
  contact,
  defaultValue = {},
  ...props
}) => {
  const intl = useIntl();
  const toaster = useToast();
  const [isShown, setIsShown] = useState(props.isShown);
  const [createdContact, setCreatedContact] = useState<IContact>();
  useEffect(() => {
    setIsShown(props.isShown);
  }, [props.isShown]);

  const close = () => {
    setIsShown(false);
  };

  const { contactStore, relationStore } = useStores();

  const {
    register,
    reset,
    control,
    errors,
    setError,
    handleSubmit,
    formState,
  } = useForm<FormValues>();

  useEffect(() => {
    if (!isShown) return;
    if (contact) {
      reset(getSnapshot(contact));
    } else {
      reset(defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact, isShown]);

  const onSubmit = async ({ company, title, ...values }: FormValues) => {
    if (contact) {
      // Update contact
      try {
        await contactStore.updateItem(contact, values);
        close();
        // Invalidate caches of relations
        relationStore.invalidateCaches();
      } catch (err) {
        // TODO: Handle error
        handleError(err);
      }
    } else {
      // Create new contact
      try {
        const contact = await contactStore.createItem(values);
        if (company) {
          // Try to get the newly created relation (if the email matches a company domain)
          const relationsCache = await relationStore.getAll({
            filter: { contact: contact.id, company },
          });
          const relation = relationsCache.items.find(
            (rel: IRelation) =>
              getSnapshot(rel).contact === contact.id &&
              getSnapshot(rel).company === company
          );
          // If we found a relation, update its title.
          if (relation) {
            relationStore.updateItem(relation, {
              title,
            });
          } else {
            // Otherwise, add contact to company with title
            await relationStore
              .createItem({
                contact: contact.id,
                company,
                title,
              })
              .catch(() => {});
          }
        }
        setCreatedContact(contact);
        close();
        // Invalidate caches of relations
        relationStore.invalidateCaches();
      } catch (err) {
        // TODO: Handle error
        handleError(err);
      }
    }
  };

  const handleError = (err: any) => {
    if (err.response && err.response.data && err.response.data) {
      if (err.response.data['emails']) {
        setError('emails', {
          message: intl.formatMessage({
            defaultMessage: 'A contact with that email already exist',
          }),
        });
      }
    } else {
      toaster.showDefaultError(err);
    }
  };

  return (
    <Dialog
      title={contact ? 'Rediger kontaktperson' : 'Opret kontaktperson'}
      isShown={isShown}
      onClose={() => props.onClose(createdContact)}
      actions={
        <>
          <Button onClick={() => close()} disabled={formState.isSubmitting}>
            Cancel
          </Button>
          <Button
            primary
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            {contact ? 'Gem ændringer' : 'Opret kontaktperson'}
          </Button>
        </>
      }
    >
      <Grid padding={majorScale(2)}>
        <Heading size={300} className="col-span-12">
          Detaljer
        </Heading>

        <FormField className="col-span-9" label="Navn" inline={true}>
          <InputGroup
            placeholder="Fornavn og efternavn"
            ref={register({
              required: 'Du skal skrive din kontaktpersons navn',
            })}
            name="name"
            isInvalid={!!errors.name}
            validationMessage={errors.name && errors.name.message}
          />
        </FormField>

        <FormField
          className="col-span-9"
          label="Kontaktoplysninger"
          inline={true}
        >
          <Controller
            control={control}
            defaultValue={[]}
            rules={{
              validate: {
                length: (value: string[]) => value.length > 0,
                email: (v: string[]) =>
                  v.reduce<boolean>((p, n) => p && validateEmail(n), true),
              },
            }}
            name="emails"
            render={({ onChange, onBlur, value }) => (
              <TagInput
                tagIntent="none"
                placeholder="Emails"
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                isInvalid={!!errors.emails}
                validationMessage={
                  !!errors.emails
                    ? (errors.emails as any).message
                      ? (errors.emails as any).message
                      : intl.formatMessage({
                          defaultMessage:
                            'You must provide at least one email for your contact',
                        })
                    : ''
                }
                marginBottom={minorScale(1)}
              />
            )}
          />
          <InputGroup
            placeholder={intl.formatMessage({
              defaultMessage: 'Phone',
            })}
            ref={register}
            name="phone"
          />
          <InputGroup
            placeholder={intl.formatMessage({
              defaultMessage: 'Mobile',
            })}
            ref={register}
            name="mobile"
          />
        </FormField>

        {!contact && (
          <>
            <FormField
              className="col-span-9"
              label="Virksomhed"
              inline={true}
              hint="Vælg en virksomhed kontaktpersonen er tilknyttet"
            >
              <Controller
                control={control}
                name="company"
                defaultValue={null}
                render={({ onChange, value }) => (
                  <CompanySelect
                    onSelect={(company) => onChange(company.id)}
                    selected={value}
                  />
                )}
              />
            </FormField>
            <FormField className="col-span-9" label="Titel" inline={true}>
              <InputGroup
                placeholder="Direktør, Ejer, Salgschef, etc."
                ref={register}
                name="title"
              />
            </FormField>{' '}
          </>
        )}
      </Grid>

      <Grid
        borderTop={`2px solid ${defaultColors.gray[100]}`}
        padding={majorScale(2)}
      >
        <Heading size={300} className="col-span-12">
          Administrering
        </Heading>

        <FormField className="col-span-9" label="Reference" inline={true}>
          <InputGroup
            placeholder="En reference"
            name="reference"
            ref={register}
          />
        </FormField>

        <FormField className="col-span-9" label="Beskrivelse" inline={true}>
          <TextArea
            placeholder="En kort beskrivelse af kontaktpersonen"
            name="description"
            ref={register}
            maxLength={500}
          />
        </FormField>

        <FormField
          className="col-span-9"
          label="Ansvarlig"
          inline={true}
          hint="Vælg en fra dit team som er ansvarlig af kontaktpersonen"
        >
          <Controller
            control={control}
            name="responsible"
            defaultValue={[]}
            render={({ onChange, value, onBlur }) => (
              <UsersInput
                onBlur={onBlur}
                value={value}
                onChange={(users) => {
                  onChange(users.map((c) => c.id));
                }}
              />
            )}
          />
        </FormField>

        <FormField className="col-span-9" label="Tags" inline>
          <Controller
            control={control}
            name="tags"
            defaultValue={[]}
            render={({ onChange, value, onBlur }) => (
              <TagInput value={value} onChange={onChange} onBlur={onBlur} />
            )}
          />
        </FormField>
      </Grid>
    </Dialog>
  );
};
